import gradientEhrIcon from 'design-system/src/assets/icons/gradientEhr.png';
import gradientLetterIcon from 'design-system/src/assets/icons/gradientLetter.png';
import gradientProviderIcon from 'design-system/src/assets/icons/gradientProvider.png';
import gradientOfficeIcon from 'design-system/src/assets/icons/gradientOffice.png';
import gradientPhoneIcon from 'design-system/src/assets/icons/gradientPhone.png';
import gradientPrescriptionIcon from 'design-system/src/assets/icons/gradientRx.png';
import gradientBellIcon from 'design-system/src/assets/icons/gradientBell.png';

/* Note: This is the only href that is re-used in the OnboardingPage, so the rest are kept local. */
export const NOTIFICATION_PREFERENCES_SETTINGS_PATH = '/settings?tab=notifications';

export const ONBOARDING_STEP_COPY = {
  addUsers: {
    title: 'Invite your teammates',
    description: 'Bring your team into Tandem to manage prescriptions, enrollments, and PAs together.',
  },
  addProviders: {
    title: 'Add more providers',
    description: "Enter each provider's National Provider Identifier (NPI) so we can match prescriptions correctly.",
  },
  addPhoneNumbers: {
    title: 'Add office numbers',
    description: 'Add any phone numbers that may appear on prescriptions for accurate routing.',
  },
  ehrIntegration: {
    description:
      'Tandem can automatically extract patient and insurance information so you can complete prior authorizations faster.',
  },
  addOffices: {
    title: 'Add your office address',
    description: 'Ensures materials arrive and prescriptions stay properly matched to your office.',
  },
  sendFirstPrescription: {
    title: 'Queue up prescriptions',
    description: "You can begin sending prescriptions now — we'll hold them until your account is verified.",
  },
  notificationPreferences: {
    title: 'Set up your notification preferences',
    description: 'Choose which prescriptions and providers you want notifications for.',
  },
} as const;

export enum OnboardingStepModalKey {
  EhrIntegration = 'ehrIntegration',
  AddUsers = 'addUsers',
  AddProviders = 'addProviders',
  AddOffices = 'addOffices',
  AddPhoneNumbers = 'addPhoneNumbers',
  SendFirstPrescription = 'sendFirstPrescription',
  NotificationPreferences = 'notificationPreferences',
}

export type OnboardingStepDefinition = {
  /**
   * Unique identifier for the step
   */
  key: OnboardingStepModalKey;
  /**
   * Returns the display title for the step
   */
  title: (ctx: { ehrDisplayName: string }) => string;
  /**
   * Description shown below the title
   */
  description: string;
  /**
   * Gradient icon PNG source
   */
  icon: string;
  /**
   * Whether this step is only shown to admin users
   */
  adminOnly: boolean;
  /**
   * Destination URL when the card is clicked
   */
  href: string;
};

export const ONBOARDING_STEP_DEFINITIONS: OnboardingStepDefinition[] = [
  {
    key: OnboardingStepModalKey.AddUsers,
    title: () => ONBOARDING_STEP_COPY.addUsers.title,
    description: ONBOARDING_STEP_COPY.addUsers.description,
    icon: gradientLetterIcon,
    adminOnly: true,
    href: '/settings?tab=users',
  },
  {
    key: OnboardingStepModalKey.AddProviders,
    title: () => ONBOARDING_STEP_COPY.addProviders.title,
    description: ONBOARDING_STEP_COPY.addProviders.description,
    icon: gradientProviderIcon,
    adminOnly: true,
    href: '/settings?tab=providers',
  },
  {
    key: OnboardingStepModalKey.AddPhoneNumbers,
    title: () => ONBOARDING_STEP_COPY.addPhoneNumbers.title,
    description: ONBOARDING_STEP_COPY.addPhoneNumbers.description,
    icon: gradientPhoneIcon,
    adminOnly: false,
    href: '/settings?tab=officeNumbers',
  },
  {
    key: OnboardingStepModalKey.EhrIntegration,
    title: ({ ehrDisplayName }) => `Connect to ${ehrDisplayName}`,
    description: ONBOARDING_STEP_COPY.ehrIntegration.description,
    icon: gradientEhrIcon,
    adminOnly: false,
    href: '/settings?tab=ehrIntegration',
  },
  {
    key: OnboardingStepModalKey.AddOffices,
    title: () => ONBOARDING_STEP_COPY.addOffices.title,
    description: ONBOARDING_STEP_COPY.addOffices.description,
    icon: gradientOfficeIcon,
    adminOnly: true,
    href: '/settings?tab=offices',
  },
  {
    key: OnboardingStepModalKey.SendFirstPrescription,
    title: () => ONBOARDING_STEP_COPY.sendFirstPrescription.title,
    description: ONBOARDING_STEP_COPY.sendFirstPrescription.description,
    icon: gradientPrescriptionIcon,
    adminOnly: false,
    href: '/prescriptions',
  },
  {
    key: OnboardingStepModalKey.NotificationPreferences,
    title: () => ONBOARDING_STEP_COPY.notificationPreferences.title,
    description: ONBOARDING_STEP_COPY.notificationPreferences.description,
    icon: gradientBellIcon,
    adminOnly: false,
    href: NOTIFICATION_PREFERENCES_SETTINGS_PATH,
  },
];
